import {
	type BroadcastableCommandConstructor,
	Command,
	type CommandConstructor,
	type CommandProperties,
	type CommandResult,
} from './bridge-api-command-types';

/**
 * Create a command with a stable reference sustained across JavaScript contexts.
 * @example
 * ```typescript
 * const MyCommand = createCommand('my-command');
 * ```
 *
 * @param commandName a bundle-safe identifier which can be safely used within the same JavaScript context,
 * or across multiple JavaScript contexts. Especially important to avoid (1) clashing of identifiers in the
 * same bundle / JavaScript context, and (2) stability of identifiers across multiple JavaScript contexts.
 *
 * @returns a unique class instance representing this command.
 */
export function createCommand<
	TProps extends CommandProperties,
	TResult extends CommandResult = CommandResult,
>(commandName: string): CommandConstructor<TProps, TResult> {
	return class NamedCommand extends Command<TProps, TResult> {
		/**
		 * @see {@link Command.identifier}
		 */
		static readonly identifier = commandName;
	};
}

/**
 * Create a broadcastable command with a stable identifier.
 */
export function createBroadcastableCommand<TProps extends CommandProperties>(
	commandName: string,
): BroadcastableCommandConstructor<TProps> {
	// We create a class and then assert the broadcastable marker.
	return class NamedBroadcastCommand extends Command<TProps, void> {
		static readonly identifier = commandName;
		static readonly __broadcastable = true as const;
	};
}
